import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import JobService from "../../../../services/JobService";
import { colors } from "../../../config/colors";
import { IoCloseOutline } from "react-icons/io5";

const RenewRecurringModal = ({ isOpen, onClose, job, onSuccess }) => {
    const [newRepeatTill, setNewRepeatTill] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && job?.reoccurrence?.repeatTill) {
            const currentTill = new Date(job.reoccurrence.repeatTill);
            if (!isNaN(currentTill)) {
                // Initialize with the exact current expiry date
                setNewRepeatTill(currentTill);
            }
        }
    }, [isOpen, job]);

    if (!isOpen || !job || !job.reoccurrence) return null;

    const { reoccurrence } = job;

    // Safety check for reoccurringId (in case it is populated/object vs string)
    const reoccurringId =
        job.reoccurringId?._id || job.reoccurringId?.id || job.reoccurringId;

    const currentRepeatTillDate = new Date(reoccurrence.repeatTill);

    // Simple helper to describe the current interval
    const renderRecurrenceDetails = () => {
        const type = reoccurrence.repeatEvery?.charAt(0).toUpperCase() + reoccurrence.repeatEvery?.slice(1);
        const interval = reoccurrence.repeatDifference || 1;

        let daysStr = "";
        if (reoccurrence.repeatOn && reoccurrence.repeatOn.length > 0) {
            daysStr = `(On ${reoccurrence.repeatOn.join(", ")})`;
        }

        return (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2 space-y-2">
                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-500">Recurrence Pattern</span>
                    <span className="text-sm font-semibold text-gray-800">
                        {type} {interval > 1 && `- Every ${interval} ${reoccurrence.repeatEvery}s `}
                        {daysStr}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-500">Current Expiry</span>
                    <span className="text-sm font-semibold text-gray-800">
                        {isNaN(currentRepeatTillDate) ? "Unknown" : currentRepeatTillDate.toLocaleDateString()}
                    </span>
                </div>
            </div>
        );
    };

    const handleRenew = async () => {
        if (!newRepeatTill) {
            toast.error("Please select a new termination date.");
            return;
        }

        if (newRepeatTill <= currentRepeatTillDate) {
            toast.error("New termination date must be after the current expiry date.");
            return;
        }

        // Set time to end of day if using standard full day jobs logic
        const payloadDate = new Date(newRepeatTill);
        payloadDate.setHours(23, 59, 59, 999);

        setIsSubmitting(true);
        try {
            const response = await JobService.renewRecurringJob(reoccurringId, {
                repeatTill: payloadDate.toISOString()
            });

            if (response.success || response.status === 'success') {
                toast.success("Recurring series successfully renewed!");
                onSuccess(); // Triggers a parent re-fetch
                onClose();
            } else {
                toast.error(response.message || "Failed to renew recurring series.");
            }
        } catch (error) {
            console.error("Error renewing job series:", error);
            const msg = error.response?.data?.message || "An error occurred while renewing the series.";
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-visible animate-fadeIn">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center rounded-t-xl" style={{ backgroundColor: colors.primary.headingsBody }}>
                    <h3 className="text-lg font-semibold text-white">Extend Job Reoccurrence</h3>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 transition-colors bg-white/10 hover:bg-white/20 p-1.5 rounded-md"
                    >
                        <IoCloseOutline size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-6">
                    <div>
                        <p className="text-sm text-gray-600 mb-2 font-medium">Current Series Configuration (Locked)</p>
                        {renderRecurrenceDetails()}
                        <p className="text-xs text-gray-500 mt-2">
                            To change interval or frequency, you must create a new recurring series.
                        </p>
                    </div>

                    <div className="border-t border-gray-100 pt-6">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            New "Repeat Till" Date <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                            Select the new date when you want this series to terminate. We recommend extending by no more than 3 months at a time.
                        </p>
                        <div className="relative">
                            <DatePicker
                                selected={newRepeatTill}
                                onChange={(date) => setNewRepeatTill(date)}
                                minDate={isNaN(currentRepeatTillDate) ? new Date() : currentRepeatTillDate}
                                dateFormat="yyyy-MM-dd"
                                placeholderText="Select new end date"
                                className="w-full rounded-2xl border-0 bg-white/70 px-5 py-3.5 text-gray-800 shadow-sm ring-1 ring-inset ring-gray-200 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-primary-main md:text-sm/6 transition-all duration-300 placeholder:text-gray-400 pr-12"
                                wrapperClassName="w-full"
                                calendarClassName="!w-[360px] shadow-lg border border-gray-200 rounded-lg"
                                showPopperArrow={false}
                                dayClassName={() => "!w-[45px] !h-[45px] !leading-[45px]"}
                                monthClassName={() => "!text-base"}
                                weekDayClassName={() => "!w-[45px] !h-[45px] !leading-[45px]"}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleRenew}
                        disabled={isSubmitting || !newRepeatTill}
                        className="px-6 py-2 rounded-lg text-sm font-medium text-white shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        style={{ backgroundColor: colors.highlights.callToAction }}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Extending...
                            </>
                        ) : (
                            "Extend Job Reoccurrence"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RenewRecurringModal;
